import { lazy, type ComponentType } from "react";

/**
 * Wraps React.lazy with retry-once-then-reload behavior.
 *
 * When a new build is deployed, browser tabs that loaded the old `index-*.js`
 * still reference old chunk filenames. The next dynamic import throws
 * "Failed to fetch dynamically imported module". We retry once (covers transient
 * network blips), then trigger a hard reload so the user gets the new build.
 *
 * We also use a sessionStorage flag to avoid an infinite reload loop in the
 * (rare) case the chunk genuinely doesn't exist server-side.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_KEY = "lovable:chunk-reload";
    try {
      const mod = await factory();
      // success — clear the flag so future failures can reload again
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||
        /ChunkLoadError/i.test(message);

      if (!isChunkError) throw err;

      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        // Return a never-resolving promise so React doesn't render an error
        // boundary while the page is reloading.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
